import * as React from 'react';
import GameBoard from './GameBoard.tsx';
import GameKeyboard from '../../components/GameKeyboard/GameKeyboard.js';
import {
  isAlphabetCharacter, 
  getCharacterDiffCount,
} from '../../utils/utils';
import {updateWeaverUserStatsWithWin} from '../../utils/userStatsUtils.ts';
import {logClickClearBoard, logWordEntered, logInvalidWordAttempt, logPuzzleSolve} from '../../utils/loggingUtils.ts';
import {
  setStartWordLocalStorage,
  getStartWordLocalStorage,
  setEndWordLocalStorage,
  getEndWordLocalStorage,
  setEnteredWordsLocalStorage,
  getEnteredWordsLocalStorage,
  setIsGameOverLocalStorage,
  getIsGameOverLocalStorage,
} from '../../utils/localStorageUtils.ts';
import { GameType } from '../../types/GameType.ts';
import { GameInterface } from '../../types/GameInterface';
import GameWithDictionary from '../../components/GameWithDictionary/GameWithDictionary.tsx';

interface WeaverGameProps extends GameInterface {
  startWord: string;
  endWord: string;
  dictionary: Set<string>;
}

interface GameState {
  enteredWords: string[];
  currentWord: string;
  isGameOver: boolean;
}

// Constants for WEAVER_X
const WEAVER_X_MIN_LENGTH = 2;
const WEAVER_X_MAX_LENGTH = 7;

class WeaverGame extends React.Component<WeaverGameProps, GameState> {
  keyboardListener: (event: KeyboardEvent) => void;

  constructor(props: WeaverGameProps) {
    super(props);
    this.state = {
      enteredWords: [],
      currentWord: '',
      isGameOver: false,
    };
    this.keyboardListener = this.processKeyboardEvent.bind(this);
  }

  addCharacter(character: string): void {
    if (this.state.isGameOver) {
      return;
    }

    const currentWord = this.state.currentWord;
    // Allow adding characters up to the maximum word length for the current game type
    const maxLength = this.getCurrentMaximumWordLength();
    if (currentWord.length >= maxLength) {
      return;
    }
    const newCurrentWord = currentWord + character.toLowerCase();
    this.setState({currentWord: newCurrentWord});
  }

  removeCharacter(): void {
    if (this.state.isGameOver) {
      return;
    }

    const currentWord = this.state.currentWord;
    if (currentWord.length === 0 && this.state.enteredWords.length > 0) {
      const newEnteredWords = [...this.state.enteredWords];
      const newCurrentWord = newEnteredWords.pop() || '';
      this.setState({
        enteredWords: newEnteredWords,
        currentWord: newCurrentWord,
      })
    } else {
      const newCurrentWord = currentWord.substring(0, currentWord.length - 1);
      this.setState({currentWord: newCurrentWord});
    }
  }

  getLastEnteredWordOrStart(): string {
    if (this.state.enteredWords.length > 0) {
      return this.state.enteredWords.slice(-1)[0];
    } else {
      return this.props.startWord;
    }
  }

  processEnter(): void {
    if (this.state.isGameOver) {
      return;
    }

    const currentWord = this.state.currentWord;
    const previousWord = this.getLastEnteredWordOrStart();
    
    // Validate word meets minimum length
    if (currentWord.length < this.getCurrentMinimumWordLength()) {
      return;
    }

    // Run all validation rules based on game type
    const validationError = this.validateWord(currentWord, previousWord);
    if (validationError) {
      // Log the invalid word attempt for analytics
      logInvalidWordAttempt(
        this.props.gameType, 
        this.props.gameNumber, 
        currentWord, 
        validationError, 
        this.state.enteredWords.length
      );
      this.props.setErrorToast(validationError);
      return;
    }

    const newEnteredWords = [...this.state.enteredWords, currentWord];
    
    logWordEntered(this.props.gameType, this.props.gameNumber, currentWord, newEnteredWords.length);

    if (this.isWinningWord(currentWord)) {
      logPuzzleSolve(this.props.gameType, this.props.gameNumber, newEnteredWords.length);
      
      this.setState({
        enteredWords: newEnteredWords,
        currentWord: '',
        isGameOver: true,
      });
      setIsGameOverLocalStorage(this.props.gameType, this.props.gameNumber, true);
      updateWeaverUserStatsWithWin(this.props.gameType as GameType.WEAVER | GameType.WEAVER_X, this.props.gameNumber, newEnteredWords);
      this.props.setSuccessToast('You win!');
      setTimeout(() => this.props.openGameStatsModal(), 1000);
    } else {
      this.setState({
        enteredWords: newEnteredWords,
        currentWord: '',
      });
    }
    setEnteredWordsLocalStorage(this.props.gameType, this.props.gameNumber, newEnteredWords);
  }

  isWinningWord(word: string): boolean {
    return getCharacterDiffCount(word, this.props.endWord) === 0;
  }

  getCurrentMinimumWordLength(): number {
    // For regular WEAVER, minimum length is the start word length
    // For WEAVER_X, minimum length could be different based on the rules
    if (this.props.gameType === GameType.WEAVER) {
      return this.props.startWord.length;
    } else {
      // For WEAVER_X, the minimum is either WEAVER_X_MIN_LENGTH or previousWord.length - 1
      const previousWord = this.getLastEnteredWordOrStart();
      return Math.max(WEAVER_X_MIN_LENGTH, previousWord.length - 1);
    }
  }
  
  // The maximum length that the current word being entered can be.
  getCurrentMaximumWordLength(): number {
    // For regular WEAVER, maximum length is the start word length
    // For WEAVER_X, maximum length could be different based on the rules
    if (this.props.gameType === GameType.WEAVER) {
      return this.props.startWord.length;
    } else {
      // For WEAVER_X, the maximum is either WEAVER_X_MAX_LENGTH or previousWord.length + 1
      const previousWord = this.getLastEnteredWordOrStart();
      return Math.min(WEAVER_X_MAX_LENGTH, previousWord.length + 1);
    }
  }

  // The number of blocks in a row, including invisible blocks, used for spacing.
  getRowLength(): number {
    if (this.props.gameType === GameType.WEAVER) {
      return this.props.startWord.length;
    } else {
      const maxEnteredWordLength = this.state.enteredWords.length > 0 ? Math.max(...this.state.enteredWords.map(word => word.length)) : 0;
      const maxCurrentWordLength = this.state.isGameOver ? 0 : this.getCurrentMaximumWordLength();
      return Math.max(this.props.startWord.length, this.props.endWord.length, maxEnteredWordLength, maxCurrentWordLength);
    }
  }

  validateWord(word: string, previousWord: string): string | null {
    // Dictionary validation - check if the word exists
    if (!this.isValidDictionaryWord(word)) {
      return 'Not in word list';
    }
    
    // Game type specific validations
    if (this.props.gameType === GameType.WEAVER) {
      return this.validateWeaverRules(word, previousWord);
    } else if (this.props.gameType === GameType.WEAVER_X) {
      return this.validateWeaverXRules(word, previousWord);
    }
    
    return null; // No validation errors
  }

  isValidDictionaryWord(word: string): boolean {
    // Acceptable word lengths: 2-7
    return this.props.dictionary.has(word) && word.length >= 2 && word.length <= 7;
  }

  validateWeaverRules(word: string, previousWord: string): string | null {
    // Rule 1: Word must be the same length as start word
    if (word.length !== this.props.startWord.length) {
      return `Word must be ${this.props.startWord.length} letters long`;
    }

    // Rule 2: Cannot be the same word
    const characterDiffCount = getCharacterDiffCount(word, previousWord);
    if (characterDiffCount === 0) {
      return 'Did not change any characters';
    }

    // Rule 3: Can only change exactly 1 character
    if (characterDiffCount !== 1) {
      return 'Cannot change more than 1 character';
    }

    return null; // No validation errors
  }

  validateWeaverXRules(word: string, previousWord: string): string | null {
    // Rule 1: Words must be within the acceptable length range
    const minLength = Math.max(WEAVER_X_MIN_LENGTH, previousWord.length - 1);
    const maxLength = Math.min(WEAVER_X_MAX_LENGTH, previousWord.length + 1);
    
    if (word.length < minLength || word.length > maxLength) {
      return `Word must be between ${minLength} and ${maxLength} letters long`;
    }
    
    // Rule 2: Words must either change by exactly 1 character or add/remove 1 character
    const lengthDiff = Math.abs(word.length - previousWord.length);
    
    if (lengthDiff === 0) {
      // Same length: must change exactly 1 character
      const characterDiffCount = getCharacterDiffCount(word, previousWord);
      if (characterDiffCount === 0) {
        return 'Did not change any characters';
      }
      if (characterDiffCount !== 1) {
        return 'Cannot change more than 1 character';
      }
    } else if (lengthDiff === 1) {
      // Different length: verify only adding or removing 1 character
      if (!this.isValidAddOrRemoveOperation(word, previousWord)) {
        if (word.length > previousWord.length) {
          return 'Cannot both change and add characters';
        } else {
          return 'Cannot both change and remove characters';
        }
      }
    } else {
      return 'Can only add or remove 1 character at a time';
    }

    return null; // No validation errors
  }

  isValidAddOrRemoveOperation(word: string, previousWord: string): boolean {
    // If adding a character, check if the previous word is a substring with one character missing
    // If removing a character, check if the current word is a substring with one character missing
    const longer = word.length > previousWord.length ? word : previousWord;
    const shorter = word.length > previousWord.length ? previousWord : word;
    
    // Try removing each character from the longer word and see if it matches the shorter word
    for (let i = 0; i < longer.length; i++) {
      const withoutChar = longer.substring(0, i) + longer.substring(i + 1);
      if (withoutChar === shorter) {
        return true;
      }
    }
    
    return false;
  }

  processKeyboardEvent(event: KeyboardEvent): void {
    // Ignore keyboard events when user is typing in an input field
    const target = event.target as HTMLElement;
    if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
      return;
    }
    if (event.keyCode === 8 /* DELETE */) {
      this.removeCharacter();
    } else if (event.keyCode === 13 /* ENTER */) {
      this.processEnter();
    } else if (isAlphabetCharacter(event.key)) {
      this.addCharacter(event.key);
    }
  }

  loadStateFromLocalStorage(): void {
    const storedStartWord = getStartWordLocalStorage(this.props.gameType, this.props.gameNumber);
    const storedEndWord = getEndWordLocalStorage(this.props.gameType, this.props.gameNumber);
    
    if (storedStartWord === this.props.startWord && storedEndWord === this.props.endWord) {
      // Load existing progress
      this.setState({
        enteredWords: getEnteredWordsLocalStorage(this.props.gameType, this.props.gameNumber),
        isGameOver: getIsGameOverLocalStorage(this.props.gameType, this.props.gameNumber),
        currentWord: '', // Reset current word when loading from storage
      });
    } else {
      // Initialize new game
      setStartWordLocalStorage(this.props.gameType, this.props.gameNumber, this.props.startWord);
      setEndWordLocalStorage(this.props.gameType, this.props.gameNumber, this.props.endWord);
      setEnteredWordsLocalStorage(this.props.gameType, this.props.gameNumber, []);
      setIsGameOverLocalStorage(this.props.gameType, this.props.gameNumber, false);
      
      // Reset state
      this.setState({
        enteredWords: [],
        currentWord: '',
        isGameOver: false,
      });
    }
  }

  setStateFromLocalStorage(): void {
    this.setState({
      enteredWords: getEnteredWordsLocalStorage(this.props.gameType, this.props.gameNumber),
      isGameOver: getIsGameOverLocalStorage(this.props.gameType, this.props.gameNumber),
      currentWord: '',
    });
  }

  componentDidMount(): void {
    this.loadStateFromLocalStorage();
    document.addEventListener('keydown', this.keyboardListener);
    this.props.onGameLoad();
  }

  // No componentDidUpdate needed! The key prop forces remount on game changes

  componentWillUnmount(): void {
    document.removeEventListener('keydown', this.keyboardListener);
  }

  onClearBoard(): void {
    this.setState({
      enteredWords: [],
      currentWord: '',
      isGameOver: false,
    });
    setEnteredWordsLocalStorage(this.props.gameType, this.props.gameNumber, []);
    setIsGameOverLocalStorage(this.props.gameType, this.props.gameNumber, false);
  }

  render(): React.ReactNode {
    const currentMinWordLength = this.getCurrentMinimumWordLength();
    const currentMaxWordLength = this.getCurrentMaximumWordLength();
    const rowLength = this.getRowLength();
    
    return (
      <div className='game'>
        <GameBoard 
          startWord={this.props.startWord}
          endWord={this.props.endWord} 
          enteredWords={this.state.enteredWords}
          currentWord={this.state.currentWord}
          isGameOver={this.state.isGameOver}
          onClickClearBoard={() => {
            logClickClearBoard(this.props.gameType, this.props.gameNumber);
            this.onClearBoard();
          }}
          gameType={this.props.gameType}
          currentMinWordLength={currentMinWordLength}
          currentMaxWordLength={currentMaxWordLength}
          rowLength={rowLength}
        />
        <GameKeyboard 
          onClickCharacter={(char: string) => this.addCharacter(char)}
          onClickDelete={() => this.removeCharacter()}
          onClickEnter={() => this.processEnter()}
        />
      </div>
    );
  }
}

// Export the unwrapped component for testing
export { WeaverGame as WeaverGameComponent };

// Export the HOC-wrapped component as default
export default GameWithDictionary(WeaverGame);
